import React, { useCallback, useContext, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { rem } from 'polished'

import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import { ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageGalleryThumbnailStrip from 'LuxKit/Carousel/ImageGalleryThumbnailStrip'
import ImageGalleryLightboxWithThumbnails from 'LuxKit/Carousel/ImageGalleryLightboxWithThumbnails'
import { ImageParams } from 'components/Common/ResponsiveImage'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import ModalContext from 'contexts/ModalContext'
import NestableClickable from 'components/Common/NestableClickable'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
`

const GalleryMedia = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;
  border-radius: 0;
  background: ${props => props.theme.palette.neutral.default.seven};

  img,
  picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const GalleryMediaButton = styled(NestableClickable)`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;
  border-radius: 0;
  padding: 0;
  background: ${props => props.theme.palette.neutral.default.seven};

  img,
  picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const GalleryStripWrapper = styled.div`
  overflow: hidden;
`

const SquareThumbnailStrip = styled(ImageGalleryThumbnailStrip)`
  > div > button {
    border-radius: 0;
  }

  > div > button::before,
  > div > button::after {
    border-radius: 0;
  }
`

const roomGalleryImageParams: ImageParams = {
  mobileAspectRatio: '16:9',
  tabletAspectRatio: '16:9',
  quality: 'good',
  fit: 'center',
}

const INTERACTIVE_ELEMENT_SELECTOR = 'button, a, input, textarea, select, details, summary, iframe, embed, audio, video'

interface RoomGalleryProps {
  images: Array<App.Image>
  initialIndex?: number
}

function RoomAmenitiesGallery(props: RoomGalleryProps) {
  const { images, initialIndex = 0 } = props
  const theme = useTheme()
  const highlightColor = theme.palette.highlight.primary.normalBackground
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const showModal = useContext(ModalContext)

  const handleImageChange = useCallback((index: number) => {
    setCurrentIndex(index)
  }, [])

  const handleThumbnailSelect = useCallback((index: number) => {
    carouselRef.current?.goToIndex(index, 'instant')
  }, [])

  const handleOpenLightbox = useCallback(() => {
    showModal(<ImageGalleryLightboxWithThumbnails
      images={images}
      defaultIndex={currentIndex}
    />)
  }, [currentIndex, images, showModal])

  const handleMediaClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest(INTERACTIVE_ELEMENT_SELECTOR)) return
    event.stopPropagation()
    handleOpenLightbox()
  }, [handleOpenLightbox])

  if (!images.length) {
    return (
      <GalleryContainer>
        <GalleryMedia>
          <ImageWithReservedSpace
            id={NO_IMAGE_AVAILABLE_ID}
            alt="Sorry this image is unavailable"
            mobileAspectRatio="16:9"
            tabletAspectRatio="16:9"
            fit="center"
          />
        </GalleryMedia>
      </GalleryContainer>
    )
  }

  return (
    <GalleryContainer>
      <GalleryMediaButton
        onClick={handleOpenLightbox}
        onClickCapture={handleMediaClickCapture}
        aria-label="Open room image gallery"
      >
        <ImageCarousel
          defaultIndex={initialIndex}
          imageParams={roomGalleryImageParams}
          carouselRef={carouselRef}
          images={images}
          onImageChange={handleImageChange}
        />
      </GalleryMediaButton>
      <GalleryStripWrapper>
        <SquareThumbnailStrip
          images={images}
          selectedIndex={currentIndex}
          onSelect={handleThumbnailSelect}
          showScrollArrows
          highlightColor={highlightColor}
        />
      </GalleryStripWrapper>
    </GalleryContainer>
  )
}

export default RoomAmenitiesGallery
